/**
 * @file: provider.tsx
 *
 * App-wide chat session. Mounted near the app root (above the router) for
 * every viewer — authenticated or not — so the unread badge, the
 * foreground-push handler and the inbox all share one source of truth.
 *
 * What lives here is deliberately small: the single WebSocket connection
 * manager, the unread total, and the two global "where is the viewer"
 * signals (`activeRoomId$`, `chatListActive$`). The heavy per-surface
 * state — thread list, contacts, the live-event reducer — is NOT app-wide;
 * it belongs to the inbox-scoped `ChatThreadsProvider` /
 * `ChatContactsProvider` and is created/destroyed with that surface.
 *
 * Unauthenticated viewers get a dummy identity (`platformUserId === ''`):
 * the provider still renders so consumers never crash, but the WS never
 * connects and the unread sync stays unmounted.
 */

import { refreshAccessToken, useServiceFactory } from '@euler/services/factory';
import { useCreation, useWatchValue } from '@euler/utils/hooks';
import { FC, ReactNode, memo, useCallback, useEffect, useMemo } from 'react';
import { BehaviorSubject, EMPTY, Subject, from } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';
import { ChatSessionContextProps, ChatUserIdentity } from '../types';
import { ChatWSClient } from './client';
import { UserPresenceHeartbeat } from './components/PresenceHeartbeat';
import { ChatUnreadSync } from './components/UnreadSync';
import { ChatSessionContext } from './context';
import { useChatPresences } from './hooks/useChatPresences';

/** Collapse a burst of refresh triggers into one `/chat/unread` call. */
const REFRESH_DEBOUNCE_MS = 300;

export const ChatSessionProvider: FC<{
  identity: ChatUserIdentity;
  children: ReactNode;
}> = memo(({ identity, children }) => {
  const { chatService, defaultTokenService } = useServiceFactory();
  const myPlatformUserId = identity.platformUserId;

  // Pass the factory's token service + refresher (not module globals) so
  // the WS stays consistent with whatever the app is wired with.
  const wsClient = useCreation(
    () => new ChatWSClient(defaultTokenService, refreshAccessToken),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const chatUnreadCount$ = useCreation(() => new BehaviorSubject(0), []);
  const activeRoomId$ = useCreation(
    () => new BehaviorSubject<string | null>(null),
    [],
  );
  const chatListActive$ = useCreation(() => new BehaviorSubject(false), []);
  const closedRoomIds$ = useCreation(
    () => new BehaviorSubject<ReadonlySet<string>>(new Set()),
    [],
  );
  const refreshTrigger$ = useCreation(() => new Subject<void>(), []);
  // Read-here → clear-there, across trees (see `ChatSessionContextProps`).
  const threadReadLocally$ = useCreation(() => new Subject<string>(), []);

  const { presence$, refreshPresence } = useChatPresences({
    wsClient,
    myPlatformUserId,
  });

  const setActiveRoomId = useCallback(
    (roomId: string | null) => activeRoomId$.next(roomId),
    [activeRoomId$],
  );
  const setChatListActive = useCallback(
    (active: boolean) => chatListActive$.next(active),
    [chatListActive$],
  );
  const setRoomClosed = useCallback(
    (roomId: string, closed: boolean) => {
      const cur = closedRoomIds$.getValue();
      if (closed === cur.has(roomId)) return;
      const next = new Set(cur);
      if (closed) next.add(roomId);
      else next.delete(roomId);
      closedRoomIds$.next(next);
    },
    [closedRoomIds$],
  );
  const refreshChatUnreadCount = useCallback(
    () => refreshTrigger$.next(),
    [refreshTrigger$],
  );
  const bumpChatUnread = useCallback(
    () => chatUnreadCount$.next(chatUnreadCount$.getValue() + 1),
    [chatUnreadCount$],
  );

  // Debounced reconcile against the server — the only place the API is
  // called. `switchMap` drops a stale in-flight request when a fresh
  // trigger lands, so the latest count always wins.
  useEffect(() => {
    if (!myPlatformUserId) return;
    const sub = refreshTrigger$
      .pipe(
        debounceTime(REFRESH_DEBOUNCE_MS),
        switchMap(() =>
          from(chatService.getUnread()).pipe(
            catchError(e => {
              console.error('[chat-session] refresh unread failed', e);
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe(counts => {
        const count = counts.__all__ ?? 0;
        if (count !== chatUnreadCount$.getValue()) {
          chatUnreadCount$.next(count);
        }
      });
    // Kick the initial reconcile now that we're subscribed. `ChatUnreadSync`
    // is a child, so its mount `refresh()` runs first (child effects before
    // parent) — before this subscription exists — and a plain Subject drops
    // that pre-subscribe emission, so the badge wouldn't load until the first
    // poll tick. Emitting here, after subscribing, makes it load on open.
    refreshTrigger$.next();
    return () => sub.unsubscribe();
  }, [myPlatformUserId, chatService, refreshTrigger$, chatUnreadCount$]);

  // Account switch within the same auth-context key — wipe state.
  // `!prev` skips both mount (undefined) and the initial login (''), so a
  // fresh sign-in doesn't needlessly zero an already-loading badge.
  // (`presence$` is reset by `useChatPresences`, which owns it.)
  useWatchValue(myPlatformUserId, prev => {
    if (!prev) return;
    chatUnreadCount$.next(0);
    activeRoomId$.next(null);
    chatListActive$.next(false);
    closedRoomIds$.next(new Set());
  });

  // One connection per session lifetime; tear it down on unmount.
  useEffect(() => () => wsClient.dispose(), [wsClient]);

  const value = useMemo<ChatSessionContextProps>(
    () => ({
      wsClient,
      me: identity,
      chatUnreadCount$,
      refreshChatUnreadCount,
      bumpChatUnread,
      activeRoomId$,
      setActiveRoomId,
      chatListActive$,
      setChatListActive,
      closedRoomIds$,
      setRoomClosed,
      threadReadLocally$,
      presence$,
      refreshPresence,
    }),
    [
      wsClient,
      identity,
      chatUnreadCount$,
      refreshChatUnreadCount,
      bumpChatUnread,
      activeRoomId$,
      setActiveRoomId,
      chatListActive$,
      setChatListActive,
      closedRoomIds$,
      setRoomClosed,
      threadReadLocally$,
      presence$,
      refreshPresence,
    ],
  );

  return (
    <ChatSessionContext.Provider value={value}>
      {myPlatformUserId ? (
        <>
          <ChatUnreadSync refresh={refreshChatUnreadCount} />
          <UserPresenceHeartbeat />
        </>
      ) : null}
      {children}
    </ChatSessionContext.Provider>
  );
});
